import React from 'react';
import { useForm, ControllerRenderProps } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import OneVerseLogo from '@/components/OneVerseLogo';
import { PhoneInput } from '@/components/ui/phone-input';
import { CountryDropdown } from '@/components/ui/country-dropdown';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  RegistrationFormData,
  RegistrationSchema,
  organizationTypeEnum,
} from './schema';
import { Dropdown } from '@/components/ui/dropdown';
import RecaptchaInput from './RecaptchaInput';
import { useTermsData } from '@/hooks/useTermsData';
import PasswordRequirements from '../PasswordRequirements';
import { getMeta } from '@/lib/utils';

interface RegistrationFormProps {
  onSubmit: (data: RegistrationFormData) => void;
  error: string | null;
  isLoading?: boolean;
  onClearError?: () => void;
  email?: string;
  isInvite?: boolean;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  onSubmit,
  error,
  isLoading = false,
  onClearError,
  email: invitationEmail,
  isInvite = false,
}) => {
  const { termsData, loading: termsLoading } = useTermsData();

  const form = useForm<RegistrationFormData>({
    resolver: zodResolver(RegistrationSchema),
    defaultValues: {
      email: invitationEmail || '',
      password: '',
      passwordConfirmation: '',
      firstName: '',
      middleInitial: '',
      lastName: '',
      organizationName: '',
      organizationType: undefined,
      countryOfResidence: '',
      countryOfBusiness: '',
      mobilePhone: '',
      termsAndCondition: {
        accepted: false,
        version: undefined,
        acceptedAt: undefined,
      },
      recaptchaToken: '',
    },
    mode: 'onBlur', // Change from onChange to onBlur
  });

  const handleFormSubmit = (data: RegistrationFormData) => {
    // Check if mobilePhone is just a country code and convert to empty string
    const countryCodePattern = /^\+\d{1,4}$/;
    const processedData = {
      ...data,
      mobilePhone:
        data.mobilePhone && countryCodePattern.test(data.mobilePhone.trim())
          ? ''
          : data.mobilePhone,
    };

    onSubmit(processedData);
  };

  const handleTermsAndConditionChange = (
    checked: string | boolean,
    field: ControllerRenderProps<RegistrationFormData, 'termsAndCondition'>
  ) => {
    const isChecked = Boolean(checked);
    const acceptedAt = isChecked ? new Date().toISOString() : undefined;
    field.onChange({
      accepted: isChecked,
      version: isChecked ? termsData?.version : undefined,
      acceptedAt,
    });
  };

  return (
    <>
      <OneVerseLogo className="mb-6 mt-2 mx-auto" />
      <h1 className="text-center text-2xl font-semibold text-gray-900 mb-2">
        Create your account
      </h1>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleFormSubmit)}
          onChange={() => error && onClearError?.()}
          className="w-full mt-4 space-y-5"
          noValidate
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Email Address</FormLabel>
                <FormControl required>
                  <Input {...field} autoComplete="email" disabled={isInvite} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel required>Password</FormLabel>
                <FormControl required>
                  <Input type="password" {...field} />
                </FormControl>
                <PasswordRequirements show={!!fieldState.error} />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="passwordConfirmation"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Password Confirmation</FormLabel>
                <FormControl required>
                  <Input type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>First Name</FormLabel>
                <FormControl required>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="middleInitial"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Middle Initial</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Last Name</FormLabel>
                <FormControl required>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="organizationName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organization Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="organizationType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organization Type</FormLabel>
                <FormControl>
                  <Dropdown
                    options={organizationTypeEnum.map(type => ({
                      label: type,
                      value: type,
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select an organization type"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="mobilePhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mobile Phone</FormLabel>
                <FormControl>
                  <PhoneInput
                    {...field}
                    value={field.value || ''}
                    placeholder="Enter phone number"
                    defaultCountry="US"
                  />
                </FormControl>
                <FormMessage />
                <p className="text-sm text-gray-600 mt-1">
                  If you provide your mobile number, you agree to receive SMS
                  messages for account verification/recovery. Frequency varies
                  with account activity. Message and data rates may apply. Reply
                  STOP to opt out at any time.{' '}
                  <a
                    href={
                      getMeta('privacy-policy-url') ??
                      'https://www.univo-group.com/privacy-policy/'
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-blue-600"
                  >
                    Privacy
                  </a>
                </p>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="countryOfResidence"
            render={({ field }) => (
              <FormItem>
                <FormLabel>What country do you live in?</FormLabel>
                <FormControl>
                  <CountryDropdown
                    key="country-residence"
                    defaultValue={field.value}
                    onChange={country => field.onChange(country.alpha3)}
                    placeholder="Select country"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="countryOfBusiness"
            render={({ field }) => (
              <FormItem>
                <FormLabel>What country is your business based in?</FormLabel>
                <FormControl>
                  <CountryDropdown
                    key="country-business"
                    defaultValue={field.value}
                    onChange={country => field.onChange(country.alpha3)}
                    placeholder="Select country"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="termsAndCondition"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl required>
                  <Checkbox
                    id="terms-and-conditions"
                    name="termsAndCondition"
                    checked={field.value.accepted}
                    onCheckedChange={checked =>
                      handleTermsAndConditionChange(checked, field)
                    }
                    disabled={termsLoading || !termsData}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel htmlFor="terms-and-conditions" required>
                    I agree to the&nbsp;
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Terms and Conditions
                    </a>
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <RecaptchaInput control={form.control} />

          <Button
            type="submit"
            className="w-full"
            variant="primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Creating Account...
              </div>
            ) : (
              'Submit'
            )}
          </Button>

          {error && (
            <Alert className="w-full mb-4" variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </form>
      </Form>
      <p className="mt-4 text-center text-sm text-gray-600">
        Already have an account?{' '}
        <a href="/login" className="font-medium text-blue-700 hover:underline">
          Log in
        </a>
      </p>
    </>
  );
};

export default RegistrationForm;
